import * as Sentry from '@sentry/react'
import { AvtGateway, HttpRequest } from '../../../kernel/avt.gateway'
import { GetFilterDetailResponse, GetFiltersResponse, HelaResponse } from '../domain/hela'
import { Job, ResponseCustomFields, Slots } from '../domain/job'
import { HelaJob } from '../domain/job-hela'
import { JobRepository } from '../domain/job.repository'
import { mapHelaJobToOldJob } from './mappers/hela-jobs'
import {
  convertObjectCamelCaseToSnakeCaseProperties,
  convertObjectSnakeCaseToCamelCaseProperties
} from '../../../kernel/mappers'
import { v4 as uuidv4 } from 'uuid'
import { NebulaJob } from '../domain/job-nebula'
import { mapNebulaJobToOldJob } from './mappers/nebula-jobs'

export const odinJobRepository: JobRepository = {
  getJobCurrencyByUUID: async (jobUUID: string): Promise<string> => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/odin/api/job-by-uuid/${jobUUID}`
    }

    const result = await AvtGateway.executeGetRequest({
      ...request
    })

    const jobCurrency: string = result['logisticCostCurrency']

    return jobCurrency
  },
  getJobChatToken: async (): Promise<string> => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/odin/api/jobs/chat/token`
    }
    let token: string
    try {
      const result = await AvtGateway.executeGetRequest(request)
      token = result['token']
    } catch (error) {
      return error
    }
    return token
  },
  getJobByUUID: async (jobUUID: string): Promise<Job> => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/odin/api/job-by-uuid/${jobUUID}`
    }

    const result = await AvtGateway.executeGetRequest({
      ...request
    })

    return result as Job
  },
  getJobById: async (jobId: string) => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/odin/api/job/${jobId}`
    }

    const result = await AvtGateway.executeGetRequest({
      ...request
    })

    return result as Job
  },
  getItemSlots: async (params) => {
    const request: HttpRequest = {
      method: 'POST',
      endpoint: `/nebula/jobs/availability/v2`
    }

    const userInfo = JSON.parse(localStorage.getItem('user_info'))
    const clientNumber = userInfo?.user?.client?.clientNumber

    const body = {
      ...convertObjectCamelCaseToSnakeCaseProperties(params),
      client_id: clientNumber,
      operational_models_priority: [params.operationalModelsPriority]
    }

    try {
      const result = (await AvtGateway.executePostRequest({
        ...request,
        body
      })) as SlotResponse[]

      return getSanitazedResult(result)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)

      if (errorMessage.includes('NO_CAPACITY_CONFIGURED_FOR_STORE_IN_GIVEN_RANGE')) {
        return {}
      }
      throw error
    }
  },
  rejectJobItems: async ({ jobId, items }) => {
    const itemsFormatted = items.map((item) => ({
      id: item.id,
      rejected_quantity: item.rejectedQuantity,
      claim_action: item.rejectedAction,
      comment: item.comment,
      rejection_reason: item.rejectedReason,
      origin: item.origin
    }))

    const request = {
      method: 'POST',
      body: {
        items_to_reject: itemsFormatted
      },
      endpoint: `/odin/api/job/${jobId}/rejectProducts/v2`
    }

    const result = await AvtGateway.executePostRequest({
      ...request
    })

    return result as { jobNumber: string }
  },
  createJobV2: async ({ comment, job, slotId, reasonType, reasonId, id, addDeliveryCode, additionalCardInfo }) => {
    const request: HttpRequest = {
      method: 'POST',
      body: {
        jobId: job.id,
        slotId,
        comment,
        reasonType,
        reasonId,
        id,
        addDeliveryCode,
        additionalCardInfo,
        prices: {
          subtotal: job.prices.subtotal,
          shipping_fee: job.prices.shippingFee,
          discounts: job.prices.discounts,
          taxes: job.prices.taxes,
          order_value: job.prices.orderValue,
          totalOrder: job.prices.totalOrder,
          attributes: job.prices.attributes
        }
      },
      endpoint: '/odin/api/job/createJobV2'
    }

    const result = await AvtGateway.executePostRequest<any>({
      ...request
    })

    return {
      newJobUuid: result.job_id,
      message: result.message
    } as {
      newJobUuid: string
      message: string
    }
  },
  getJob: async (uuid) => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/nebula/jobs/${uuid}`
    }

    const result = await AvtGateway.executeGetRequest<NebulaJob>({ ...request })
    const job = mapNebulaJobToOldJob(result)

    return job
  },
  getJobs: async (params) => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/hela/api/jobs`,
      queryParams: params
    }

    try {
      const result = await AvtGateway.executeGetRequest<HelaResponse<HelaJob[]>>({
        ...request
      })

      if (!result || !result.data || !Array.isArray(result.data)) {
        const msg = 'Unexpected response from getJobs from hela'
        console.warn(msg, 'Response: ', result)
        Sentry.captureMessage(msg, {
          level: 'error',
          extra: { result }
        })
        return { jobs: [], metadata: result?.metadata ?? {} }
      }

      const jobs = result.data.map((helaJob) => mapHelaJobToOldJob(helaJob))
      return { jobs, metadata: result.metadata }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { params }
      })
      throw error
    }
  },
  reschedule: async ({ jobId, slot, comment, resetSteps, reasonId, reasonType, otherReason }) => {
    const id = uuidv4()
    const request = {
      method: 'PUT',
      body: {
        jobId,
        from: slot.from,
        to: slot.to,
        back_to_storage: resetSteps,
        comment,
        reasonType,
        reasonId: reasonId,
        id,
        otherReason,
        slotId: slot?.slotId
      },
      endpoint: '/odin/api/job/reschedule/v2'
    }

    await AvtGateway.executePutRequest({
      ...request
    })
    return
  },
  getJobsFilters: async () => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/hela/api/jobs/filters`
    }

    const result = await AvtGateway.executeGetRequest<HelaResponse<GetFiltersResponse>>({
      ...request
    })

    return result
  },
  getJobsFilterDetail: async (filterReference, params) => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/hela/api/jobs/filters/${filterReference.toLowerCase()}`,
      queryParams: params
    }

    const result = await AvtGateway.executeGetRequest<HelaResponse<GetFilterDetailResponse>>({
      ...request
    })

    return result
  },
  getJobCustomFields: async (jobIdOrUuid: string) => {
    const request: HttpRequest = {
      method: 'GET',
      endpoint: `/odin/api/job/${jobIdOrUuid}/custom-fields`
    }

    const result = await AvtGateway.executeGetRequest<ResponseCustomFields>({
      ...request
    })

    return result
  }
}

function getSanitazedResult(result: SlotResponse[]) {
  return result.reduce((acc: Slots, slot) => {
    const slots = { ...acc }
    if (slot.description !== 'EXPRESS' && slot.description !== 'STATIC') return {}
    const keySlot = slot.description === 'EXPRESS' ? 'express' : 'static'
    slots[`${keySlot}`] = slots?.[`${keySlot}`]
      ? [...slots[`${keySlot}`]!, convertObjectSnakeCaseToCamelCaseProperties(slot)]
      : [convertObjectSnakeCaseToCamelCaseProperties(slot)]
    return slots
  }, {})
}

type SlotResponse = {
  description: 'STATIC' | 'EXPRESS'
  expires_at: string
  from: string
  id: string
  operational_model: string
  to: string
}
